// @ts-nocheck
import React, { useState, useCallback } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet } from 'react-native';
import DbTitlePair from '../DbTitlePair';
import DbTag from '../DbTag';
import DbAmountDisplay from '../DbAmountDisplay';
import DbIcon from '../DbIcon';
import OrderActionBar from './OrderActionBar';
import type { DbOrderCardProps, CountDisplayConfig, DisplayConfig } from './DbOrderCard.types';

/**
 * 默认显示配置
 */
const defaultDisplayConfig: Required<DisplayConfig> = {
    showHeader: true,
    showSku: true,
    showTags: true,
    showCount: true,
    showTotalAmount: true,
    showActionBar: true,
};

/**
 * 渲染数量显示（支持图标或文本）
 */
const renderCount = (count: number, config?: CountDisplayConfig) => {
    if (config?.useIcon) {
        return (
            <View style={styles.countIconContainer}>
                <DbIcon
                    name={config.icon?.name || 'close'}
                    family={config.icon?.family || 'Ionicons'}
                    size={config.icon?.size || 12}
                    color={config.icon?.color || '#999'}
                />
                <Text style={[styles.countText, config.style]}>
                    {count}
                </Text>
            </View>
        );
    }

    const format = config?.format || 'x{count}';
    return (
        <Text style={[styles.countText, config?.style]}>
            {format.replace('{count}', String(count))}
        </Text>
    );
};

/**
 * DbOrderCard - 订单卡片组件
 * 
 * 特性：
 * 1. 顶部：店铺/订单号 + 订单状态（支持自定义样式和插槽）
 * 2. 商品区域：左侧图片 + 右侧信息（支持自定义样式和插槽）
 * 3. 实付款区域（支持自定义样式和插槽）
 * 4. 操作栏：支持完全自定义或扩展
 * 
 * 商品参数通过 OrderProductInfo 对象统一维护
 * 样式通过 styles 配置对象统一管理
 */
const DbOrderCard: React.FC<DbOrderCardProps> = ({
    productInfo,
    actions = [],
    onMorePress,
    onPress,
    extra,
    actionBar,
    actionBarLeft,
    actionBarRight,
    headerSlot,
    skuSlot,
    tagsSlot,
    productSlot,
    totalAmountSlot,
    styles: customStyles,
    display,
    onSkuExpandChange,
    defaultSkuExpanded = false,
}) => {
    const [skuExpanded, setSkuExpanded] = useState(defaultSkuExpanded);

    // 合并显示配置
    const displayConfig = { ...defaultDisplayConfig, ...display };

    // 处理SKU展开/收起
    const handleSkuToggle = useCallback(() => {
        const newExpanded = !skuExpanded;
        setSkuExpanded(newExpanded);
        onSkuExpandChange?.(newExpanded);
    }, [skuExpanded, onSkuExpandChange]);

    // 渲染顶部（店铺/订单号 + 状态）
    const renderHeader = () => {
        // 如果插槽为null，不显示
        if (headerSlot === null) return null;
        // 如果提供了自定义插槽，使用插槽
        if (headerSlot !== undefined) return <View style={customStyles?.header?.container}>{headerSlot}</View>;
        // 不显示头部
        if (!displayConfig.showHeader) return null;

        const leftText = productInfo.shopName || productInfo.orderNo || '';
        if (!leftText && !productInfo.status) return null;

        return (
            <View style={[styles.header, customStyles?.header?.container]}>
                <DbTitlePair
                    layout="space-between"
                    title={{
                        text: leftText,
                        fontSize: 16,
                        fontWeight: 'bold',
                        color: '#333',
                        ...customStyles?.header?.mainTitle,
                    }}
                    subtitle={productInfo.status ? {
                        text: productInfo.status,
                        fontSize: 13,
                        color: '#FF2442',
                        ...customStyles?.header?.subTitle,
                    } : undefined}
                />
            </View>
        );
    };

    // 渲染SKU区域
    const renderSku = () => {
        // 如果插槽为null，不显示
        if (skuSlot === null) return null;
        // 如果提供了自定义插槽，使用插槽
        if (skuSlot !== undefined) return skuSlot;
        // 不显示SKU
        if (!displayConfig.showSku) return null;

        const { skuText, skuDetails, showSkuExpand } = productInfo;
        
        if (!skuText && (!skuDetails || skuDetails.length === 0)) {
            return null;
        }

        const hasExpand = showSkuExpand && skuDetails && skuDetails.length > 0;
        const displayText = skuExpanded && skuDetails 
            ? skuDetails.join(' ')
            : skuText || (skuDetails ? skuDetails.join(' ') : '');

        const countConfig = customStyles?.product?.count;

        return (
            <View style={styles.skuContainer}>
                <View style={styles.skuRow}>
                    <TouchableOpacity
                        style={styles.skuTextContainer}
                        onPress={hasExpand ? handleSkuToggle : undefined}
                        activeOpacity={hasExpand ? 0.7 : 1}
                    >
                        <DbTitlePair
                            layout="space-between"
                            title={{
                                text: displayText,
                                fontSize: 12,
                                color: '#999',
                                numberOfLines: skuExpanded ? undefined : 1,
                                ...customStyles?.product?.sku,
                            }}
                            subtitle={displayConfig.showCount ? {
                                content: renderCount(productInfo.count, countConfig),
                            } : undefined}
                        />
                    </TouchableOpacity>
                    
                    {hasExpand && (
                        <TouchableOpacity
                            style={styles.expandButton}
                            onPress={handleSkuToggle}
                            activeOpacity={0.7}
                        >
                            <DbIcon
                                name={skuExpanded ? 'chevron-up' : 'chevron-down'}
                                family="Ionicons"
                                size={14}
                                color="#999"
                            />
                        </TouchableOpacity>
                    )}
                </View>
            </View>
        );
    };

    // 渲染标签
    const renderTags = () => {
        // 如果插槽为null，不显示
        if (tagsSlot === null) return null;
        // 如果提供了自定义插槽，使用插槽
        if (tagsSlot !== undefined) return tagsSlot;
        // 不显示标签
        if (!displayConfig.showTags) return null;

        const { tags } = productInfo;
        if (!tags || tags.length === 0) return null;

        const gap = customStyles?.tags?.gap ?? 6;

        return (
            <View style={[styles.tagsContainer, { gap }, customStyles?.tags?.container]}>
                {tags.map((tag, index) => (
                    <DbTag
                        key={`${tag.text}-${index}`}
                        text={tag.text}
                        preset={tag.preset || 'guarantee'}
                        variant={tag.variant||'light'}
                        size="small"
                        color={tag.color || '#52C41A'}
                        style={styles.tag}
                    />
                ))}
            </View>
        );
    };

    // 渲染商品区域
    const renderProduct = () => {
        // 如果插槽为null，不显示
        if (productSlot === null) return null;
        // 如果提供了自定义插槽，使用插槽
        if (productSlot !== undefined) return productSlot;

        const imageWidth = productInfo.imageWidth || 80;
        const imageHeight = productInfo.imageHeight || 80;

        return (
            <View style={[styles.productContainer, customStyles?.product?.container]}>
                {/* 左侧图片 */}
                <Image
                    source={{ uri: productInfo.image }}
                    style={[
                        styles.image,
                        { width: imageWidth, height: imageHeight },
                        customStyles?.image,
                    ]}
                    resizeMode="cover"
                />

                {/* 右侧信息 */}
                <View style={styles.productInfo}>
                    {/* 第一行：标题 + 价格 */}
                    <DbTitlePair
                        layout="space-between"
                        title={{
                            text: productInfo.title,
                            fontSize: 15,
                            fontWeight: 'bold',
                            color: '#333',
                            numberOfLines: 1,
                            ...customStyles?.product?.title,
                        }}
                        subtitle={{
                            content: (
                                <DbAmountDisplay
                                    amount={productInfo.price}
                                    mainFontSize={14}
                                    subFontSize={10}
                                    mainColor="#333"
                                    subColor="#333"
                                    currency="¥"
                                />
                            ),
                        }}
                    />

                    {/* 第二行：SKU + 数量 */}
                    {renderSku()}

                    {/* 第三行：标签 */}
                    {renderTags()}

                    {/* 第四行：扩展插槽 */}
                    {extra && <View style={styles.extraContainer}>{extra}</View>}
                </View>
            </View>
        );
    };

    // 渲染实付款
    const renderTotalAmount = () => {
        // 如果插槽为null，不显示
        if (totalAmountSlot === null) return null;
        // 如果提供了自定义插槽，使用插槽
        if (totalAmountSlot !== undefined) return totalAmountSlot;
        // 不显示实付款
        if (!displayConfig.showTotalAmount) return null;
        if (!productInfo.totalAmount) return null;

        const label = customStyles?.totalAmount?.label || '实付款';

        return (
            <View style={[styles.totalContainer, customStyles?.totalAmount?.container]}>
                <View style={styles.totalContent}>
                    <Text style={[styles.totalLabel, customStyles?.totalAmount?.labelStyle]}>
                        {label}
                    </Text>
                    <DbAmountDisplay
                        amount={productInfo.totalAmount}
                        mainFontSize={16}
                        subFontSize={11}
                        mainColor="#333"
                        subColor="#333"
                        currency="¥"
                        mainFontWeight="bold"
                    />
                </View>
            </View>
        );
    };

    // 渲染操作栏
    const renderActionBar = () => {
        if (!displayConfig.showActionBar) return null;

        if (actionBar) {
            // 自定义操作栏（完全替换）
            return <View style={styles.actionBarContainer}>{actionBar}</View>;
        }

        if (actions.length === 0 && !actionBarLeft && !actionBarRight) {
            return null;
        }

        return (
            <View style={styles.actionBarWrapper}>
                {/* 左侧扩展 */}
                {actionBarLeft && <View style={styles.actionBarSide}>{actionBarLeft}</View>}
                
                {/* 右侧按钮区域 */}
                <View style={styles.actionBarCenter}>
                    {actionBarRight && <View style={styles.actionBarRight}>{actionBarRight}</View>}
                    {actions.length > 0 && (
                        <OrderActionBar
                            actions={actions}
                            onMorePress={onMorePress}
                            buttonRadius={6}
                        />
                    )}
                </View>
            </View>
        );
    };

    // 如果有点击事件，用 TouchableOpacity 作为根容器
    const Container = onPress ? TouchableOpacity : View;
    const containerProps = onPress ? { onPress, activeOpacity: 0.9 } : {};

    return (
        <Container style={[styles.container, customStyles?.container]} {...containerProps}>
            {/* 顶部：店铺/订单号 + 状态 */}
            {renderHeader()}

            {/* 商品区域 */}
            {renderProduct()}

            {/* 实付款 */}
            {renderTotalAmount()}

            {/* 操作栏 */}
            {renderActionBar()}
        </Container>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#fff',
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 12,
        marginBottom: 12,
        overflow: 'hidden',  // 确保子元素不超出圆角边界
        // marginVertical: 4,
        // borderBottomWidth: 0,
        borderBottomColor: '#fff',
    },
    header: {
        marginBottom: 12,
    },
    productContainer: {
        flexDirection: 'row',
        gap: 12,
    },
    image: {
        borderRadius: 4,
        backgroundColor: '#f5f5f5',
    },
    productInfo: {
        flex: 1,
        gap: 4,
    },
    skuContainer: {
        marginTop: 2,
    },
    skuRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    skuTextContainer: {
        flex: 1,
    },
    expandButton: {
        padding: 4,
        marginLeft: 4,
    },
    countIconContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
    },
    countText: {
        fontSize: 12,
        color: '#999',
    },
    tagsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 2,
    },
    tag: {
        marginRight: 0,
    },
    extraContainer: {
        marginTop: 4,
    },
    totalContainer: {
        // marginTop: 12,
        // paddingTop: 12,
        // borderTopWidth: StyleSheet.hairlineWidth,
        // borderTopColor: '#f0f0f0',
        // alignItems: 'flex-end',
    },
    totalContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    totalLabel: {
        fontSize: 13,
        color: '#333',
        marginRight: 4,
    },
    actionBarContainer: {
        paddingHorizontal:12
        // marginTop: 12,
        // paddingTop: 12,
        // borderTopWidth: StyleSheet.hairlineWidth,
        // borderTopColor: '#f0f0f0',
    },
    actionBarWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex:1
        // marginTop: 12,
        // paddingTop: 12,
        // borderTopWidth: StyleSheet.hairlineWidth,
        // borderTopColor: '#f0f0f0',
    },
    actionBarSide: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    actionBarCenter: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flex: 1,
    },
    actionBarRight: {
        marginRight: 8,
    },
});

export default DbOrderCard;
export { OrderActionBar };
export type {
    DbOrderCardProps,
    OrderProductInfo,
    OrderActionButton,
    OrderActionBarProps,
    CountDisplayConfig,
    HeaderStyleConfig,
    ProductStyleConfig,
    TagsStyleConfig,
    TotalAmountStyleConfig,
    DisplayConfig,
} from './DbOrderCard.types';
